import React, { useState } from 'react';
import { Card, Table, Button, Input, Space, Tag, Typography, Row, Col, Select, Badge } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined, SettingOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

interface Role {
  key: string;
  id: string;
  roleName: string;
  roleCode: string;
  description: string;
  userCount: number;
  status: 'active' | 'inactive';
  permissions: string[];
  createTime: string;
  updateTime: string;
}

const RoleManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);

  // 模拟数据
  const mockData: Role[] = [
    {
      key: '1',
      id: 'ROLE001',
      roleName: '超级管理员',
      roleCode: 'SUPER_ADMIN',
      description: '拥有系统所有权限，可以管理所有功能模块',
      userCount: 1,
      status: 'active',
      permissions: ['用户管理', '角色管理', '流量管理', '设备管理', '套餐管理', '订单管理', '商户管理', '财务管理'],
      createTime: '2024-01-01',
      updateTime: '2024-01-10',
    },
    {
      key: '2',
      id: 'ROLE002',
      roleName: '运营管理员',
      roleCode: 'OPERATION_ADMIN',
      description: '负责日常运营管理，包括流量、设备、套餐等管理',
      userCount: 3,
      status: 'active',
      permissions: ['流量管理', '设备管理', '套餐管理', '订单管理'],
      createTime: '2024-01-02',
      updateTime: '2024-01-05',
    },
    {
      key: '3',
      id: 'ROLE003',
      roleName: '财务管理员',
      roleCode: 'FINANCE_ADMIN',
      description: '负责财务管理，包括订单、商户、财务报表等',
      userCount: 2,
      status: 'active',
      permissions: ['订单管理', '商户管理', '财务管理'],
      createTime: '2024-01-03',
      updateTime: '2024-01-08',
    },
    {
      key: '4',
      id: 'ROLE004',
      roleName: '普通用户',
      roleCode: 'NORMAL_USER',
      description: '基础用户权限，只能查看部分数据',
      userCount: 5,
      status: 'active',
      permissions: ['查看数据'],
      createTime: '2024-01-04',
      updateTime: '2024-01-04',
    },
  ];

  const columns = [
    {
      title: '角色名称',
      dataIndex: 'roleName',
      key: 'roleName',
      render: (name: string, record: Role) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.roleCode}</div>
        </div>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
    },
    {
      title: '用户数量',
      dataIndex: 'userCount',
      key: 'userCount',
      render: (count: number) => (
        <Badge count={count} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap = {
          active: { text: '启用', color: 'green' },
          inactive: { text: '停用', color: 'red' },
        };
        const statusInfo = statusMap[status as keyof typeof statusMap];
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions: string[]) => (
        <div>
          {permissions.slice(0, 3).map((permission, index) => (
            <Tag key={index}>{permission}</Tag>
          ))}
          {permissions.length > 3 && (
            <Tag>+{permissions.length - 3}</Tag>
          )}
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
    },
    {
      title: '更新时间',
      dataIndex: 'updateTime',
      key: 'updateTime',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: Role) => (
        <Space size="middle">
          <Button type="link" size="small">查看</Button>
          <Button type="link" size="small">编辑</Button>
          <Button type="link" size="small" icon={<SettingOutlined />}>权限设置</Button>
          {record.status === 'active' ? (
            <Button type="link" size="small" danger>停用</Button>
          ) : (
            <Button type="link" size="small">启用</Button>
          )}
          <Button type="link" size="small" danger>删除</Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (value: string) => {
    console.log('搜索:', value);
  };

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>角色管理</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<PlusOutlined />} type="primary">
                新增角色
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Search
                placeholder="搜索角色名称、角色代码"
                allowClear
                enterButton={<SearchOutlined />}
                style={{ width: 300 }}
                onSearch={handleSearch}
              />
              <Select placeholder="角色状态" style={{ width: 120 }} allowClear>
                <Option value="active">启用</Option>
                <Option value="inactive">停用</Option>
              </Select>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button>批量操作</Button>
              <Button>导出数据</Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={mockData}
          loading={loading}
          pagination={{
            total: mockData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
        />
      </Card>
    </div>
  );
};

export default RoleManagement; 